import { useEffect, useRef, useState } from 'react'
import { Tooltip } from '@mui/material'
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import SpaRoundedIcon from '@mui/icons-material/SpaRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import { EVENT_TYPE_CONSULTATION } from 'src/consumer/constants'
import { getDiagnosisFromCache } from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/diagnosis/apptDiagnosisCache'
import { DIAGNOSIS_UPDATE_EVENT } from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/diagnosis/apptDiagnosisManager'
import {
  PREV_DIAGNOSIS_UPDATE_EVENT,
  getPreviousDiagnosis,
} from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/diagnosis/prevDiagnosisManager'

/**
 * Short label chip for a completed appointment row. The label is role-aware and always carries an
 * abbreviation prefix:
 *   - Dx (Diagnosis) / Cc (Complaint)  → doctor consultations
 *   - Tx (Therapy) / PC (Presenting concern) → therapist sessions
 *
 * Reads exclusively from the IndexedDB cache — the whole day's labels are primed in ONE bulk call
 * by ConsultsTab (see apptDiagnosisManager.primeDiagnosesForDay), so there is NO per-row network
 * request here. The chip re-reads the cache when the day is primed, and self-gates to the mentor
 * flow + events likely to have documentation. Renders nothing when there is no label.
 */

const KIND_CONFIG = {
  dx: {
    abbr: 'Dx',
    full: 'Diagnosis',
    Icon: LocalHospitalRoundedIcon,
    fg: '#3730a3',
    bg: 'rgba(99, 102, 241, 0.12)',
  },
  cc: {
    abbr: 'Cc',
    full: 'Complaint',
    Icon: ChatBubbleOutlineRoundedIcon,
    fg: '#9a3412',
    bg: 'rgba(249, 115, 22, 0.12)',
  },
  pc: {
    abbr: 'PC',
    full: 'Presenting concern',
    Icon: ChatBubbleOutlineRoundedIcon,
    fg: '#9a3412',
    bg: 'rgba(249, 115, 22, 0.12)',
  },
  tx: {
    abbr: 'Tx',
    full: 'Therapy',
    Icon: SpaRoundedIcon,
    fg: '#115e59',
    bg: 'rgba(20, 184, 166, 0.14)',
  },
}

export default function EventDiagnosisChip({ bookedSlot, sessionStatus, maxWidth = 220 }) {
  const eventId = bookedSlot?.id
  const patientId = bookedSlot?.patientId
  const isMentorFlow =
    typeof window !== 'undefined' && window.location.pathname.includes('/mentor-home/')
  // Current-visit label: shown for completed events or ones already carrying documentation.
  const currentEligible = Boolean(
    eventId &&
      isMentorFlow &&
      (sessionStatus === 'completed' || (bookedSlot?.reportContentSize || 0) > 0),
  )
  // Previous-visit fallback: a doctor consultation that isn't done yet and isn't a first visit —
  // there may be no Dx/Cc today, so we hint the patient's most recent previous-visit label.
  const prevEligible = Boolean(
    patientId &&
      isMentorFlow &&
      bookedSlot?.eventType === EVENT_TYPE_CONSULTATION &&
      sessionStatus !== 'completed' &&
      bookedSlot?.patientMeetingSeqNumber !== 1,
  )

  // undefined = unknown/loading, { label: '', kind } = checked-none, { label, kind } = has label
  const [entry, setEntry] = useState(undefined)
  // Previous-visit label: undefined = not primed yet, null = none, { kind, text } = has one
  const [prevEntry, setPrevEntry] = useState(undefined)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!currentEligible) return undefined

    let cancelled = false
    const readCache = () => {
      getDiagnosisFromCache(eventId).then((rec) => {
        if (cancelled || !mountedRef.current || !rec) return
        setEntry({ label: rec.label || '', kind: rec.kind || 'dx' })
      })
    }

    readCache()
    // Re-read when the day's labels get primed into the cache.
    const onUpdate = () => readCache()
    window.addEventListener(DIAGNOSIS_UPDATE_EVENT, onUpdate)

    // Safety net: never leave a permanent skeleton if priming fails / never runs.
    const timeoutId = setTimeout(() => {
      if (!cancelled && mountedRef.current) {
        setEntry((prev) => (prev === undefined ? { label: '', kind: 'dx' } : prev))
      }
    }, 12000)

    return () => {
      cancelled = true
      window.removeEventListener(DIAGNOSIS_UPDATE_EVENT, onUpdate)
      clearTimeout(timeoutId)
    }
  }, [currentEligible, eventId])

  useEffect(() => {
    if (!prevEligible) {
      setPrevEntry(null)
      return undefined
    }
    const read = () => {
      const p = getPreviousDiagnosis(patientId)
      setPrevEntry(p === undefined ? undefined : p || null)
    }
    read()
    const onUpdate = () => read()
    window.addEventListener(PREV_DIAGNOSIS_UPDATE_EVENT, onUpdate)
    return () => window.removeEventListener(PREV_DIAGNOSIS_UPDATE_EVENT, onUpdate)
  }, [prevEligible, patientId])

  // 1) Current-visit label takes precedence when present.
  if (currentEligible && entry === undefined) {
    // Loading: subtle skeleton until the day is primed.
    return (
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block',
          width: 54,
          height: 12,
          borderRadius: 6,
          backgroundColor: 'rgba(100, 116, 139, 0.12)',
        }}
      />
    )
  }

  if (entry?.label) {
    const cfg = KIND_CONFIG[entry.kind] || KIND_CONFIG.dx
    const { Icon } = cfg
    return (
      <Tooltip title={`${cfg.full}: ${entry.label}`} arrow>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            maxWidth,
            fontSize: '11px',
            fontWeight: 600,
            color: cfg.fg,
            backgroundColor: cfg.bg,
            padding: '1px 7px 1px 5px',
            borderRadius: '6px',
            lineHeight: 1.5,
          }}
        >
          <Icon style={{ fontSize: 13, flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {cfg.abbr}: {entry.label}
          </span>
        </span>
      </Tooltip>
    )
  }

  // 2) No current label → hint the previous-visit label (loads async; simply appears when ready).
  if (prevEligible && prevEntry?.text) {
    const cfg = KIND_CONFIG[prevEntry.kind] || KIND_CONFIG.dx
    return (
      <Tooltip title={`Previous visit — ${cfg.full}: ${prevEntry.text}`} arrow>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            maxWidth,
            fontSize: '11px',
            fontWeight: 600,
            color: '#64748b',
            backgroundColor: 'rgba(100, 116, 139, 0.1)',
            border: '1px dashed rgba(100, 116, 139, 0.4)',
            padding: '1px 7px 1px 5px',
            borderRadius: '6px',
            lineHeight: 1.5,
          }}
        >
          <HistoryRoundedIcon style={{ fontSize: 13, flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Prev {cfg.abbr}: {prevEntry.text}
          </span>
        </span>
      </Tooltip>
    )
  }

  return null
}
